import Signpost from "@/components/Signpost";
import HeroText from "@/components/HeroText";
import NextSteps from "@/components/NextSteps";
import Video from "@/components/Video";
import Container from "@/components/Container";
import CardGrid from "@/components/Card";
import Text from "@/components/Text";
import { Product } from "@/types/products";
import { useEffect, useState } from "preact/hooks";
import { QuestionAnswer } from "@/types/questions";
import LoadingProducts from "@/modules/LoadingProducts";
import { trackComplete, trackLinkClick } from "@/utils/adobeAnalytics";

interface SummaryProps {
  products: Record<string, Product>;
  answers: Record<string, QuestionAnswer[]>;
}

const Summary = ({ products, answers }: SummaryProps) => {
  const [recommendedProducts, setRecommendedProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    trackComplete();

    const topThreeProducts = Object.entries(products)
      .sort(([, a], [, b]) => b.score - a.score)
      .slice(0, 3)
      .map(([_, value]) => value);

    setRecommendedProducts(topThreeProducts);
  }, [products]);

  if (recommendedProducts.length < 3 || isLoading) {
    return (
      <LoadingProducts
        delay={4000}
        onReady={() => setIsLoading(false)}
        loadingText="Determining your next step"
      />
    );
  }

  const summaryText = {
    degree: answers["1"][0].summaryText,
    experience: answers["4"][0].summaryText,
    interest: answers["6"].map((a) => a.text).join(" and "),
    completedCourses:
      answers["2"][0].id === "none_of_these"
        ? ""
        : `, and as you have already completed our ${answers["2"]
            .map((a) => a.text)
            .join(" and ")}`,
  };

  const summarySentence = `
  <p>Based on your ${summaryText.degree}, ${summaryText.experience} and interest in ${summaryText.interest}${summaryText.completedCourses}, we think your best option is the
${recommendedProducts[0].name}.</p>`;

  const handleHeroCTAClick = () => {
    trackLinkClick(
      recommendedProducts[0].linkLabel,
      recommendedProducts[0].url
    );
    window.open(recommendedProducts[0].url, '_self');
  };

  return (
    <>
      <Container className="lg:lc-mt-24">
        <div className="lc-flex lc-flex-col lc-gap-2 lc-animate-fadeTop lc-relative">
          <div className="lc-flex lc-flex-col md:lc-flex-row lc-gap-4 lc-font-azo-sans lc-z-[1]">
            <HeroText
              heading="Your recommended path..."
              copy={summarySentence}
              buttonText={recommendedProducts[0].linkLabel}
              buttonLink={handleHeroCTAClick}
              iconPosition="none"
              buttonSize="sm"
            />
            {recommendedProducts[0].videoId && <Video
              accountId="1183701590001"
              playerId="rkcysOOFe"
              videoId={recommendedProducts[0].videoId}
              topPick={true}
              overviewTitle={`${recommendedProducts[0].name} overview`}
              overviewDescription={`Watch this short overview from ${
                recommendedProducts[0].name === "Private Equity Certificate"
                  ? "Azmi Özünlü, Senior Director, Content Development Curriculum & Learning"
                  : "Chris Wiese, CFA, Managing Director of Education"
              }, CFA Institute.`}
            />}
          </div>
          <div className="lc-relative lc-flex lc-flex-col">
            <Signpost
              text="Download the full CFA Institute brochure"
              buttonText="Download the brochure"
              href="/discover/learning-compass-brochure-download"
            />
            <div className="lc-bg-bar-background lc-bg-contain lc-bg-clip-content lc-bg-no-repeat lc-bg-bottom lc-absolute lc--bottom-72 lc--top-36 lc-w-[1800px] lc-mx-auto lc--left-full lc--right-full lc-z-0"></div>
          </div>
          <div className="lc-mt-8 md:lc-mt-24 lc-z-[1]">
            <Text
              text="Our top 3 recommendations compared"
              size="sm"
              upperCase={true}
            />
            <CardGrid products={recommendedProducts} />
          </div>
          <NextSteps products={recommendedProducts} />
          <Signpost
            text="Haven't found what you're looking for?"
            buttonText="View all programs"
            href="/programs/all-programs"
          />
        </div>
      </Container>
    </>
  );
};
export default Summary;
